import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingService } from '@core/services/loading.service';

/**
 * Application-wide busy indicator.
 *
 * Rendered once at the app root and driven by {@link LoadingService}, so any HTTP request (or an
 * explicitly tracked long-running task) dims the screen and shows a spinner. Blocking pointer
 * events is deliberate: it stops users from re-clicking a button or reloading the page because
 * nothing appeared to happen.
 *
 * If the overlay stays up long enough for {@link LoadingService.stuck} to flip true — the app is
 * most likely wedged on a stale bundle or a request that will never resolve — it automatically
 * redirects to `/clear`, which wipes local/session storage, unregisters the service worker, and
 * signs the user out, rather than leaving them stuck behind an unresponsive spinner forever.
 */
@Component({
  selector: 'qms-loading-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinnerModule],
  template: `
    @if (loading.visible()) {
      <div class="overlay" role="status" aria-live="polite" aria-label="Loading">
        <div class="panel">
          <mat-progress-spinner mode="indeterminate" diameter="48" strokeWidth="4" />
          <span class="label">Working…</span>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .overlay {
        position: fixed;
        inset: 0;
        z-index: 3000;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.55);
        backdrop-filter: blur(1px);
        cursor: progress;
        animation: qms-overlay-in 120ms ease-out;
      }
      .panel {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        padding: 1.25rem 1.75rem;
        border-radius: 4px;
        background: #fff;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
      }
      .label {
        font-size: 0.85rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: #424242;
      }
      @keyframes qms-overlay-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .overlay {
          animation: none;
        }
      }
    `,
  ],
})
export class LoadingOverlayComponent {
  protected readonly loading = inject(LoadingService);
  private readonly router = inject(Router);

  constructor() {
    effect(() => {
      if (this.loading.stuck() && !this.router.url.startsWith('/clear')) {
        this.router.navigateByUrl('/clear');
      }
    });
  }
}
